<template>
  <simulador-card
    :title="$t('tools.conservativeAnalysisTitle')"
    :info="$t('tools.conservativeAnalysisInfo')"
  >
    <simulador-field
      id="porcentagem-meta"
      :label="$t('tools.conservativeGoalLabel')"
      type="number"
      :value="porcentagemMeta"
      :min="1"
      :max="100"
      unit="%"
      @input="updatePorcentagemMeta"
    />

    <simulador-field
      id="faturamento-conservador"
      :label="$t('tools.conservativeRevenueLabel')"
      :value="faturamentoConservador"
      unit="R$"
      unit-position="left"
      :formula="$t('tools.conservativeRevenueFormula')"
      :is-result="true"
    />
  </simulador-card>
</template>

<script>
import SimuladorCard from "./SimuladorCard.vue";
import SimuladorField from "./SimuladorField.vue";

export default {
  name: "AnaliseConservadora",
  components: {
    SimuladorCard,
    SimuladorField
  },
  props: {
    value: {
      type: Object,
      default: () => ({
        porcentagemMeta: 50,
        faturamentoConservador: "0,00"
      })
    },
    faturamentoPotencial: {
      type: [Number, String],
      default: 0
    }
  },
  data() {
    return {
      // Propriedades locais para garantir reatividade
      valorPorcentagemMeta: this.value.porcentagemMeta,
      valorFaturamentoConservador: this.value.faturamentoConservador
    };
  },
  computed: {
    porcentagemMeta: {
      get() {
        return this.valorPorcentagemMeta;
      },
      set(valor) {
        this.valorPorcentagemMeta = valor;
        this.$emit("input", {
          ...this.value,
          porcentagemMeta: valor
        });
        this.calcularFaturamentoConservador();
      }
    },
    faturamentoConservador: {
      get() {
        return this.valorFaturamentoConservador;
      },
      set(valor) {
        this.valorFaturamentoConservador = valor;
        this.$emit("input", {
          ...this.value,
          faturamentoConservador: valor
        });
      }
    }
  },
  methods: {
    updatePorcentagemMeta(valor) {
      this.porcentagemMeta = valor;
    },
    formatarMoeda(valor) {
      // Garantir que valor seja um número
      const num = parseFloat(valor) || 0;
      return num.toLocaleString("pt-BR", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
    },
    extrairNumero(valor) {
      // Converter um valor em string (formato moeda BR) para número
      if (typeof valor === "number") return valor;

      if (typeof valor === "string") {
        // Remover caracteres não numéricos, exceto vírgula e ponto
        const limpo = valor.replace(/[^\d,.]/g, "");
        // Substituir vírgula por ponto (padrão JS)
        const formatado = limpo.replace(/\./g, "").replace(",", ".");
        return parseFloat(formatado) || 0;
      }

      return 0;
    },
    calcularFaturamentoConservador() {
      // Obter o valor numérico do faturamento potencial
      let valorPotencial = 0;

      if (typeof this.faturamentoPotencial === "number") {
        valorPotencial = this.faturamentoPotencial;
      } else if (typeof this.faturamentoPotencial === "string") {
        valorPotencial = this.extrairNumero(this.faturamentoPotencial);
      }

      // Se o valor for zero ou negativo, usar um valor padrão para evitar cálculos incorretos
      if (valorPotencial <= 0) {
        valorPotencial = 33600; // Valor padrão como fallback
      }

      // Obter a porcentagem da meta
      const percentual = parseFloat(this.porcentagemMeta) || 0;

      // Calcular o faturamento conservador
      const resultado = (valorPotencial * percentual) / 100;

      // Formatar e atualizar o valor
      const resultadoFormatado = this.formatarMoeda(resultado);
      this.faturamentoConservador = resultadoFormatado;

      // Emitir evento com o valor calculado
      this.$emit("faturamento-conservador-atualizado", resultado);

      return resultado;
    }
  },
  watch: {
    // Observar mudanças no valor do faturamento potencial
    faturamentoPotencial: {
      handler(newVal, oldVal) {
        // Recalcular apenas se houver uma mudança real no valor
        if (newVal !== oldVal) {
          this.calcularFaturamentoConservador();
        }
      },
      immediate: true,
      deep: true
    }
  },
  mounted() {
    // Inicializar o cálculo quando o componente for montado
    this.calcularFaturamentoConservador();
  }
};
</script>

<style>
/* Estilos específicos do componente, se necessário */
</style>
